import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { CardHeader, useMediaQuery, useTheme } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';

import ws from '../../../assets/icons/whatsapp-primary-dark.svg';
import { DOCUMENTS_TYPES } from '../../../constants.js/generals';
import EditClientDataModal from '../EditClientDataModal/EditClientDataModal';
import { useSnackbar } from 'notistack';
import { successToast } from '../../../constants.js/misc';

const ClientInfo = ({
    clientId,
    fullname,
    first_name,
    last_name,
    phone_number,
    email,
    dni,
    document_type,
    address,
    address_detail,
    city_id,
    city,
    handleClickContact,
}) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [dialogOpen, setDialogOpen] = useState(false);

    const handleDialogClose = () => setDialogOpen(false);
    const handleDataUpdate = () => {
        enqueueSnackbar(t('OrderDetail.submitSuccess'), successToast);
        setTimeout(() => window.location.reload(), 1000);
        handleDialogClose();
    };

    const openModal = () => setDialogOpen(true);

    const documentTypeClient = DOCUMENTS_TYPES[document_type]?.name || '';

    // Responsive styles
    const cardStyles = {
        marginBottom: isMobile ? '16px' : '21px'
    };

    const cardHeaderStyles = {
        padding: isMobile ? '12px 16px 8px 16px' : '17px 20px 13px 20px',
    };

    const cardContentStyles = {
        padding: isMobile ? '0 12px 16px 12px' : '0 16px 25px 16px',
    };

    const editLinkStyles = {
        fontSize: isMobile ? '14px' : '16px',
        cursor: 'pointer',
        textAlign: 'right',
        padding: isMobile ? '8px' : '16px',
    };

    const whatsappContainerStyles = {
        margin: '10px 0 0 0',
        display: 'flex',
        columnGap: '7px',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        alignItems: isMobile ? 'flex-start' : 'center',
    };

    const whatsappLinkStyles = {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        wordBreak: 'break-word',
    };

    const whatsappImageStyles = {
        marginRight: '5px',
        width: isMobile ? '16px' : '20px',
        height: isMobile ? '16px' : '20px',
    };

    const typographyVariant = isMobile ? 'body2' : 'body1';
    const headerVariant = isMobile ? 'subtitle1' : 'h6';

    return (
        <>
            <Card elevation={2} style={cardStyles}>
                <Grid container alignItems="center" justifyContent="center">
                    <Grid item xs={8} sm={9} md={10}>
                        <CardHeader
                            titleTypographyProps={{ variant: headerVariant }}
                            title={t('ClientDetail.details.title')}
                            style={cardHeaderStyles}
                        />
                    </Grid>
                    <Grid item xs={4} sm={3} md={2}>
                        <Link style={editLinkStyles} target="_blank" onClick={openModal}>
                            {t('ClientDetail.details.edit')}
                        </Link>
                    </Grid>
                </Grid>
                <CardContent style={cardContentStyles}>
                    <Grid container style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={{ wordBreak: 'break-word' }}
                        >
                            <strong>{t('ClientDetail.details.name')}</strong> {fullname}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={whatsappContainerStyles}
                        >
                            <span>{t('ClientDetail.details.whatsapp')}</span>
                            <Link
                                variant="button"
                                color="textSecondary"
                                style={whatsappLinkStyles}
                                target="_blank"
                                onClick={handleClickContact}
                            >
                                <>
                                    <img src={ws} alt={phone_number} style={whatsappImageStyles} />+
                                    {phone_number}
                                </>
                            </Link>
                        </Typography>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={{ wordBreak: 'break-all' }}
                        >
                            <strong>{t('ClientDetail.details.email')}</strong> {email}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={{ wordBreak: 'break-word' }}
                        >
                            <strong>{t('ClientDetail.details.id')}</strong> {documentTypeClient}{' '}
                            {dni}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={{ wordBreak: 'break-word' }}
                        >
                            <strong>{t('ClientDetail.details.address')}</strong> {address}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={{ wordBreak: 'break-word' }}
                        >
                            <strong>{t('ClientDetail.details.addressDetail')}</strong>{' '}
                            {address_detail}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={{ wordBreak: 'break-word' }}
                        >
                            <strong>{t('ClientDetail.details.city')}</strong> {city?.name}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={{ wordBreak: 'break-word' }}
                        >
                            <strong>{t('ClientDetail.details.department')}</strong> {city?.state}
                        </Typography>
                        <Typography
                            gutterBottom
                            variant={typographyVariant}
                            style={{ wordBreak: 'break-word' }}
                        >
                            <strong>{t('ClientDetail.details.country')}</strong>{' '}
                            {city?.country_code}
                        </Typography>
                    </Grid>
                </CardContent>
            </Card>
            <EditClientDataModal
                dataToEdit={{
                    clientId,
                    first_name,
                    last_name,
                    phone_number,
                    email,
                    dni,
                    document_type,
                    address,
                    address_detail,
                    city_id,
                }}
                open={dialogOpen}
                isNoClose={false}
                handleClose={handleDialogClose}
                handleSuccess={handleDataUpdate}
                type={1}
            />
        </>
    );
};

export default connect()(ClientInfo);
